import ShotBatchExample from '../../curriculum/components/ShotBatchExample';
import { uploadCurriculumShotBatch } from '../../curriculum/services/curriculumService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LevelLite, CurriculumResource, WorksheetAgeGroup } from '../../curriculum/adapters/types';
import { codeManagedCurricula, makeDemoExperience, scaffoldedCurricula, scaffoldedDemoExperiences, type ManagedCurriculum } from '../../curriculum/managedCurricula';
import { listManagedCurricula, saveCurriculumPublicMetadata, saveManagedCurriculum, uploadCurriculumFile } from '../../curriculum/services/curriculumService';
import HtmlDocumentEditor from '../../../shared/ui/HtmlDocumentEditor';

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const inputClass = 'curriculum-admin-input';
const bundledContent = [...codeManagedCurricula, ...scaffoldedCurricula, ...scaffoldedDemoExperiences];
const worksheetAgeGroups: { id: WorksheetAgeGroup; label: string }[] = [
  { id: 'k-2', label: 'K–2' },
  { id: '3-5', label: '3–5' },
  { id: '6-8', label: '6–8' },
  { id: 'k-8', label: 'K–8' },
];

const DemoDurationRange: React.FC<{
  min?: number;
  max?: number;
  onChange: (min: number, max: number) => void;
}> = ({ min, max, onChange }) => {
  const safeMin = min || 1;
  const safeMax = max || Math.max(safeMin, 4);
  return <label className="curriculum-duration-compact">
    <span>Duration</span>
    <span className="curriculum-duration-box">
      <input aria-label="Minimum duration in hours" type="number" min="0.5" max="24" step="0.5" value={safeMin} onChange={(event) => { const next = Number(event.target.value); if (next > 0) onChange(next, Math.max(next, safeMax)); }} />
      <i aria-hidden="true">–</i>
      <input aria-label="Maximum duration in hours" type="number" min="0.5" max="24" step="0.5" value={safeMax} onChange={(event) => { const next = Number(event.target.value); if (next > 0) onChange(Math.min(safeMin, next), next); }} />
      <b>hours</b>
    </span>
  </label>;
};

const AdminCurriculumTab: React.FC = () => {
  const [curricula, setCurricula] = useState<ManagedCurriculum[]>(clone(bundledContent));
  const [curriculumId, setCurriculumId] = useState(scaffoldedCurricula[0].id);
  const [contentMode, setContentMode] = useState<'curriculum' | 'demo_experience'>('curriculum');
  const [classIndex, setClassIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingField, setUploadingField] = useState<'video' | 'worksheet' | 'reference-pdf' | 'student-artifact-example' | 'ai-generated-example' | 'public-badge' | null>(null);
  const [uploadingWorksheetAgeGroup, setUploadingWorksheetAgeGroup] = useState<WorksheetAgeGroup | null>(null);
  const [draggingWorksheetAgeGroup, setDraggingWorksheetAgeGroup] = useState<WorksheetAgeGroup | null>(null);
  const [draggingLessonExample, setDraggingLessonExample] = useState<'student-artifact-example' | 'ai-generated-example' | null>(null);
  const [draggingReferencePdf, setDraggingReferencePdf] = useState(false);
  const [draggingPublicBadge, setDraggingPublicBadge] = useState(false);
  const [draggingPreflightVideo, setDraggingPreflightVideo] = useState(false);
  const [uploadingActivity, setUploadingActivity] = useState<{ missionId: string; kind: 'video' | 'document' } | null>(null);
  const [uploadingResourceIndex, setUploadingResourceIndex] = useState<number | null>(null);
  const [draggingResourceIndex, setDraggingResourceIndex] = useState<number | null>(null);
  const [lessonLoopExpanded, setLessonLoopExpanded] = useState(true);
  const [expandedMissionIds, setExpandedMissionIds] = useState<Set<string>>(new Set());
  const [newDemoIds, setNewDemoIds] = useState<Set<string>>(new Set());
  const [draggedClassIndex, setDraggedClassIndex] = useState<number | null>(null);
  const [classDropIndex, setClassDropIndex] = useState<number | null>(null);
  const missionElements = useRef(new Map<string, HTMLElement>());

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const managed = await listManagedCurricula();
      // Curriculum IDs are case-insensitive throughout routing. Normalize here
      // so the legacy `musicWinter2026` document cannot appear as a second,
      // incomplete curriculum beside the canonical `musicwinter2026` entry.
      const byId = new Map(managed.map((item) => [item.id.toLowerCase(), item]));
      const bundledIds = new Set(bundledContent.map((item) => item.id.toLowerCase()));
      const merged = bundledContent.map((fallback) => {
        const saved = byId.get(fallback.id.toLowerCase());
        if (!saved) return clone(fallback);
        return clone({
          ...fallback,
          ...saved,
          id: fallback.id,
          contentSource: fallback.contentSource || saved.contentSource,
          levels: fallback.contentSource === 'code' ? fallback.levels : (saved.levels?.length ? saved.levels : fallback.levels),
        });
      });
      managed.filter((item) => !bundledIds.has(item.id.toLowerCase())).forEach((item) => merged.push(clone(item)));
      setCurricula(merged.map((item) => ({
        ...item,
        kind: item.kind || 'curriculum',
        levels: item.levels.map((lesson, index) => ({
          ...lesson,
          priority: Number(lesson.priority) > 0 ? Number(lesson.priority) : index + 1,
        })),
      })));
      setNewDemoIds(new Set());
    } catch (error: any) {
      setMessage(error?.message || 'Could not load saved curriculum data. Showing bundled scaffolds.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setExpandedMissionIds(new Set());
  }, [curriculumId, classIndex]);

  const curriculum = useMemo(() => curricula.find((item) => item.id === curriculumId) || curricula[0], [curricula, curriculumId]);
  const visibleCurricula = useMemo(() => curricula.filter((item) => (item.kind || 'curriculum') === contentMode), [contentMode, curricula]);
  const level = curriculum?.levels?.[classIndex];
  const demoDurationMin = curriculum?.durationMinHours ?? curriculum?.durationHours ?? 1;
  const demoDurationMax = curriculum?.durationMaxHours ?? curriculum?.durationHours ?? Math.max(demoDurationMin, 4);
  const isCodeManaged = curriculum?.contentSource === 'code';

  const updateCurriculum = (updater: (item: ManagedCurriculum) => ManagedCurriculum) => {
    setCurricula((items) => items.map((item) => item.id === curriculumId ? updater(clone(item)) : item));
  };
  const chooseMode = (mode: 'curriculum' | 'demo_experience') => {
    setContentMode(mode);
    const first = curricula.find((item) => (item.kind || 'curriculum') === mode);
    if (first) setCurriculumId(first.id);
    setClassIndex(0);
  };
  const addDemoExperience = () => {
    const id = `demo-experience-${Date.now()}`;
    const experience = makeDemoExperience(id, 'New Demo Experience');
    setCurricula((items) => [...items, experience]);
    setNewDemoIds((ids) => new Set(ids).add(id));
    setCurriculumId(id);
    setClassIndex(0);
  };
  const addClass = () => {
    const now = Date.now();
    const nextIndex = curriculum.levels.length;
    const classNumber = nextIndex + 1;
    const nextPriority = Math.max(0, ...curriculum.levels.map((item) => Number(item.priority) || 0)) + 1;
    const newLevel: LevelLite = {
      id: `class-${now}`,
      title: `Class ${classNumber}: To be developed`,
      priority: nextPriority,
      kind: 'required',
      briefSummary: 'Lesson content coming soon.',
      teacherNotes: '',
      additionalNotes: '',
      iCanStatement: '',
      slideDeckUrl: '',
      videoUrl: '',
      resources: [],
      missions: [],
    };
    updateCurriculum((item) => ({ ...item, levels: [...item.levels, newLevel] }));
    setClassIndex(nextIndex);
  };
  const removeClass = async (index: number) => {
    if (curriculum.levels.length <= 1) {
      setMessage('A curriculum must contain at least one class.');
      return;
    }
    const classToRemove = curriculum.levels[index];
    if (!await window.appConfirm(`Remove “${classToRemove.title}” from this curriculum?`)) return;
    updateCurriculum((item) => ({ ...item, levels: item.levels.filter((_, itemIndex) => itemIndex !== index) }));
    setClassIndex((selectedIndex) => {
      if (selectedIndex > index) return selectedIndex - 1;
      if (selectedIndex === index) return Math.min(index, curriculum.levels.length - 2);
      return selectedIndex;
    });
  };
  const moveClass = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= curriculum.levels.length || toIndex >= curriculum.levels.length) return;
    const selectedId = curriculum.levels[classIndex]?.id;
    updateCurriculum((item) => {
      const levels = [...item.levels];
      const [moved] = levels.splice(fromIndex, 1);
      levels.splice(toIndex, 0, moved);
      return { ...item, levels };
    });
    const reordered = [...curriculum.levels];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setClassIndex(Math.max(0, reordered.findIndex((item) => item.id === selectedId)));
  };
  const exportCurriculumCsv = () => {
    const plainText = (value: unknown) => {
      const element = document.createElement('div');
      element.innerHTML = String(value || '');
      return (element.textContent || '').replace(/\s+/g, ' ').trim();
    };
    const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const headers = [
      'Class number', 'Class ID', 'Class title', 'Priority', 'Class kind', 'Brief summary', 'Parent-facing summary', 'I can statement',
      'Thinking skill', 'Refrain', 'Google Slides URL', 'Video URL', 'Teacher notes',
      'Additional notes', 'K-2 worksheet URL', '3-5 worksheet URL', '6-8 worksheet URL',
      'K-8 worksheet URL', 'Student artifact example URL', 'AI-generated example URL',
      'Resources', 'Activities',
    ];
    const rows = curriculum.levels.map((item, index) => {
      const resources = (item.resources || []).map((resource) =>
        [resource.kind || 'link', resource.title, resource.url].filter(Boolean).join(' | ')
      ).join('\n');
      const activities = (item.missions || []).map((mission: any, missionIndex: number) => {
        const title = mission.title || mission.text || 'Untitled activity';
        const details = [
          `${missionIndex + 1}. ${title}`,
          mission.missionType || mission.type,
          mission.plannedMinutes ? `${mission.plannedMinutes} min` : '',
          plainText(mission.teachingNotes),
        ].filter(Boolean);
        return details.join(' | ');
      }).join('\n');
      return [
        index + 1,
        item.id,
        item.title,
        item.priority ?? 0,
        item.kind || 'required',
        plainText(item.briefSummary),
        plainText(item.parentSummary),
        plainText(item.iCanStatement),
        plainText(item.thinkingSkill),
        plainText(item.refrain),
        item.slideDeckUrl || '',
        item.video?.url || item.videoUrl || '',
        plainText(item.teacherNotes),
        plainText(item.additionalNotes),
        item.worksheetsByAgeGroup?.['k-2']?.url || '',
        item.worksheetsByAgeGroup?.['3-5']?.url || '',
        item.worksheetsByAgeGroup?.['6-8']?.url || '',
        item.worksheetsByAgeGroup?.['k-8']?.url || item.worksheet?.url || '',
        item.studentArtifactExample?.url || '',
        item.aiGeneratedExample?.url || '',
        resources,
        activities,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = curriculum.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'curriculum';
    link.href = url;
    link.download = `${safeTitle}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const updateLevel = (patch: Partial<LevelLite>) => updateCurriculum((item) => {
    item.levels[classIndex] = { ...item.levels[classIndex], ...patch };
    return item;
  });
  const updateLessonExampleDescription = (field: 'studentArtifactExample' | 'aiGeneratedExample', description: string) => {
    const existing = level?.[field];
    updateLevel({
      [field]: {
        id: existing?.id || `${curriculum.id}-${level.id}-${field}`,
        title: existing?.title || (field === 'studentArtifactExample' ? 'Student artifact example' : 'AI-generated example'),
        url: existing?.url || '',
        kind: existing?.kind || 'document',
        ...existing,
        description,
      },
    });
  };
  const updateAiExampleUrl = (url: string) => {
    const existing = level.aiGeneratedExample;
    let title = existing?.title || 'AI-generated example link';
    try {
      const parsed = new URL(url);
      title = parsed.pathname.split('/').filter(Boolean).pop() || parsed.hostname;
    } catch {
      // Keep a friendly title while the URL is incomplete.
    }
    updateLevel({
      aiGeneratedExample: {
        id: existing?.id || `${curriculum.id}-${level.id}-aiGeneratedExample`,
        title,
        url,
        kind: 'link',
        description: existing?.description || '',
      },
    });
  };
  const updateMission = (missionIndex: number, patch: Record<string, unknown>) => {
    const missions = clone(level?.missions || []);
    missions[missionIndex] = { ...missions[missionIndex], ...patch };
    updateLevel({ missions });
  };
  const addMission = () => {
    const missionId = `${curriculum.id}-${level.id}-activity-${Date.now()}`;
    const missions = clone(level?.missions || []);
    missions.push({
      id: missionId,
      type: 'completion',
      text: 'New activity',
      title: 'New activity',
      plannedMinutes: 10,
      teachingNotes: '',
      missionType: 'activity',
    });
    updateLevel({ missions });
    setLessonLoopExpanded(true);
    setExpandedMissionIds((ids) => new Set(ids).add(missionId));
    window.requestAnimationFrame(() => {
      const element = missionElements.current.get(missionId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element?.querySelector<HTMLInputElement>('input[data-activity-name]')?.focus({ preventScroll: true });
    });
  };
  const addLessonLoop = () => {
    const now = Date.now();
    updateLevel({ missions: [{
      id: `${curriculum.id}-${level.id}-activity-${now}`,
      type: 'completion',
      text: 'New activity',
      title: 'New activity',
      plannedMinutes: 10,
      teachingNotes: '',
      missionType: 'activity',
    }] });
    setLessonLoopExpanded(true);
  };
  const removeMission = async (missionIndex: number) => {
    const mission = (level?.missions || [])[missionIndex];
    if (!mission || !await window.appConfirm(`Remove “${mission.title || mission.text}” from this class?`)) return;
    updateLevel({ missions: (level?.missions || []).filter((_, index) => index !== missionIndex) });
  };
  const moveMission = (missionIndex: number, direction: -1 | 1) => {
    const target = missionIndex + direction;
    const missions = clone(level?.missions || []);
    if (target < 0 || target >= missions.length) return;
    [missions[missionIndex], missions[target]] = [missions[target], missions[missionIndex]];
    updateLevel({ missions });
  };
  const toggleMission = (missionId: string) => {
    setExpandedMissionIds((ids) => {
      const next = new Set(ids);
      if (next.has(missionId)) next.delete(missionId);
      else next.add(missionId);
      return next;
    });
  };
  const updateResource = (resourceIndex: number, patch: Partial<CurriculumResource>) => {
    const resources = clone(level?.resources || []);
    resources[resourceIndex] = { ...resources[resourceIndex], ...patch };
    updateLevel({ resources });
  };
  const addResource = () => updateLevel({ resources: [...(level?.resources || []), { id: `resource-${Date.now()}`, title: 'New resource', url: '', kind: 'link' }] });
  const removeResource = (index: number) => updateLevel({ resources: (level?.resources || []).filter((_, i) => i !== index) });
  const uploadResource = async (resourceIndex: number, file: File) => {
    setUploadingResourceIndex(resourceIndex);
    setDraggingResourceIndex(null);
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({
        curriculumId: curriculum.id,
        levelId: level.id,
        file,
        kind: 'resource',
        scopeId: String((level.resources || [])[resourceIndex]?.id || resourceIndex),
      });
      const current = (level.resources || [])[resourceIndex];
      updateResource(resourceIndex, {
        ...uploaded,
        id: current?.id || uploaded.id,
        title: !current?.title || current.title === 'New resource' ? file.name : current.title,
        kind: 'download',
      });
      setMessage(`${file.name} uploaded. Save the curriculum to publish this change.`);
    } catch (error: any) {
      setMessage(error?.message || 'Could not upload the resource.');
    } finally {
      setUploadingResourceIndex(null);
    }
  };
  const uploadClassFile = async (file: File, kind: 'video' | 'worksheet') => {
    setUploadingField(kind);
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({ curriculumId: curriculum.id, levelId: level.id, file, kind });
      if (kind === 'video') updateLevel({ video: uploaded, videoUrl: uploaded.url });
      else updateLevel({ worksheet: uploaded });
      setMessage(`${kind === 'video' ? 'Video' : contentMode === 'demo_experience' ? 'Flyer' : 'Worksheet'} uploaded. Save to publish this change.`);
    } catch (error: any) {
      setMessage(error?.message || `Could not upload ${kind}.`);
    } finally {
      setUploadingField(null);
    }
  };
  const uploadPreflightVideo = (file: File) => {
    const hasVideoType = file.type.startsWith('video/');
    const hasVideoExtension = /\.(mp4|mov|m4v|webm|avi|mpeg|mpg|ogv)$/i.test(file.name);
    if (!hasVideoType && !hasVideoExtension) {
      setMessage('Please drop a video file into the pre-flight video section.');
      return;
    }
    void uploadClassFile(file, 'video');
  };
  const setAgeGroupWorksheet = (ageGroup: WorksheetAgeGroup, resource: CurriculumResource) => {
    const worksheetsByAgeGroup = { ...(level.worksheetsByAgeGroup || {}), [ageGroup]: resource };
    updateLevel(ageGroup === 'k-8' ? { worksheetsByAgeGroup, worksheet: resource } : { worksheetsByAgeGroup });
  };
  const uploadAgeGroupWorksheet = async (file: File, ageGroup: WorksheetAgeGroup) => {
    setUploadingWorksheetAgeGroup(ageGroup);
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({ curriculumId: curriculum.id, levelId: level.id, file, kind: 'worksheet', scopeId: ageGroup });
      setAgeGroupWorksheet(ageGroup, uploaded);
      setMessage(`${worksheetAgeGroups.find((item) => item.id === ageGroup)?.label} worksheet uploaded. Save the curriculum to publish this change.`);
    } catch (error: any) {
      setMessage(error?.message || 'Could not upload the worksheet.');
    } finally {
      setUploadingWorksheetAgeGroup(null);
    }
  };
  const uploadReferencePdf = async (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setMessage('The main reference must be a PDF file.');
      return;
    }
    setUploadingField('reference-pdf');
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({ curriculumId: curriculum.id, levelId: level.id, file, kind: 'reference-pdf' });
      updateCurriculum((item) => ({ ...item, referencePdf: { ...uploaded, kind: 'document' } }));
      setMessage('Main reference PDF uploaded. Save the demo experience to publish this change.');
    } catch (error: any) {
      setMessage(error?.message || 'Could not upload the reference PDF.');
    } finally {
      setUploadingField(null);
    }
  };
  const uploadPublicBadge = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setMessage('The program badge must be an image file.');
      return;
    }
    setUploadingField('public-badge');
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({ curriculumId: curriculum.id, levelId: 'public', file, kind: 'public-badge' });
      updateCurriculum((item) => ({ ...item, publicBadgeUrl: uploaded.url }));
      setMessage('Website badge uploaded. Save the curriculum to publish this change.');
    } catch (error: any) {
      setMessage(error?.message || 'Could not upload the website badge.');
    } finally {
      setUploadingField(null);
    }
  };
  const uploadLessonExample = async (file: File, kind: 'student-artifact-example' | 'ai-generated-example') => {
    setUploadingField(kind);
    setMessage('');
    try {
      const uploaded = kind === 'ai-generated-example' && /\.zip$/i.test(file.name)
        ? await uploadCurriculumShotBatch({ curriculumId: curriculum.id, levelId: level.id, file, onProgress: setMessage })
        : await uploadCurriculumFile({ curriculumId: curriculum.id, levelId: level.id, file, kind });
      const field = kind === 'student-artifact-example' ? 'studentArtifactExample' : 'aiGeneratedExample';
      updateCurriculum(item => {
        const target = item.levels.find(candidate => candidate.id === level.id);
        if (target) target[field] = { ...uploaded, description: target[field]?.description || '' };
        return item;
      });
      setMessage(`${kind === 'student-artifact-example' ? 'Student artifact' : 'AI-generated'} example uploaded. Save the curriculum to publish this change.`);
    } catch (error: any) {
      setMessage(error?.message || 'Could not upload the lesson example.');
    } finally {
      setUploadingField(null);
    }
  };
  const uploadActivityFile = async (missionIndex: number, file: File, kind: 'video' | 'document') => {
    const mission = (level.missions || [])[missionIndex];
    if (!mission) return;
    const missionId = String(mission.id);
    setUploadingActivity({ missionId, kind });
    setMessage('');
    try {
      const uploaded = await uploadCurriculumFile({
        curriculumId: curriculum.id,
        levelId: level.id,
        file,
        kind: kind === 'video' ? 'video' : 'worksheet',
        scopeId: missionId,
      });
      updateMission(missionIndex, {
        attachments: [...(mission.attachments || []), { ...uploaded, kind }],
      });
      setMessage(`${kind === 'video' ? 'Video' : 'Document'} attached to “${mission.title || mission.text}”. Save the curriculum to publish this change.`);
    } catch (error: any) {
      setMessage(error?.message || `Could not upload the activity ${kind}.`);
    } finally {
      setUploadingActivity(null);
    }
  };
  const removeActivityAttachment = (missionIndex: number, attachmentIndex: number) => {
    const mission = (level.missions || [])[missionIndex];
    updateMission(missionIndex, { attachments: (mission?.attachments || []).filter((_: unknown, index: number) => index !== attachmentIndex) });
  };

  const save = async () => {
    if (!curriculum) return;
    let curriculumToSave = curriculum;
    if (curriculum.kind === 'demo_experience') {
      const min = curriculum.durationMinHours ?? curriculum.durationHours ?? 1;
      const max = curriculum.durationMaxHours ?? curriculum.durationHours ?? Math.max(min, 4);
      if (!min || !max || min <= 0 || max <= 0) {
        setMessage('Enter both a minimum and maximum demo duration.');
        return;
      }
      if (min > max) {
        setMessage('Minimum duration cannot be greater than maximum duration.');
        return;
      }
      curriculumToSave = { ...curriculum, durationMinHours: min, durationMaxHours: max };
    }
    setBusy(true); setMessage('');
    try {
      if (isCodeManaged) await saveCurriculumPublicMetadata(curriculumToSave);
      else await saveManagedCurriculum(curriculumToSave);
      if (curriculum.kind === 'demo_experience') {
        setNewDemoIds((ids) => {
          const next = new Set(ids);
          next.delete(curriculum.id);
          return next;
        });
      }
      setMessage(`${isCodeManaged ? 'Public program details' : curriculum.kind === 'demo_experience' ? 'Demo experience' : 'Curriculum'} saved. Public program pages update automatically.`);
    } catch (error: any) { setMessage(error?.message || 'Could not save curriculum.'); }
    finally { setBusy(false); }
  };

  const setDemoPublished = async (published: boolean) => {
    const updated = { ...curriculum, published };
    setBusy(true);
    setMessage('');
    try {
      await saveManagedCurriculum(updated);
      updateCurriculum(() => updated);
      setNewDemoIds((ids) => {
        const next = new Set(ids);
        next.delete(curriculum.id);
        return next;
      });
      setMessage(published ? 'Demo experience published and available for squad assignment.' : 'Demo experience moved back to draft.');
    } catch (error: any) {
      setMessage(error?.message || `Could not ${published ? 'publish' : 'unpublish'} this demo experience.`);
    } finally {
      setBusy(false);
    }
  };

  if (!curriculum || !level) return <div className="curriculum-admin"><p>No curriculum scaffold found.</p></div>;

  return (
    <div className="curriculum-admin">
      <header className="curriculum-admin-header">
        <div><h2>Curriculum</h2><p>Manage lesson decks, pre-flight materials, class resources, and activity notes.</p></div>
        <div className="curriculum-admin-header-actions">
          <button type="button" className="curriculum-admin-secondary" onClick={exportCurriculumCsv}>Export CSV</button>
          <button type="button" className="curriculum-admin-save" onClick={save} disabled={busy}>{busy ? 'Saving…' : isCodeManaged ? 'Save public details' : contentMode === 'demo_experience' ? 'Save demo' : 'Save curriculum'}</button>
        </div>
      </header>
      <div className="curriculum-admin-tabs" role="tablist" aria-label="Curriculum content type">
        <button type="button" role="tab" aria-selected={contentMode === 'curriculum'} onClick={() => chooseMode('curriculum')}>Curricula</button>
        <button type="button" role="tab" aria-selected={contentMode === 'demo_experience'} onClick={() => chooseMode('demo_experience')}>Demo experiences</button>
      </div>
      {message && <div className="curriculum-admin-message">{message}</div>}

      <div className="curriculum-admin-layout">
        <aside className="curriculum-admin-sidebar">
          <label>{contentMode === 'demo_experience' ? 'Select a demo to edit' : 'Curriculum'}<select value={curriculumId} onChange={(event) => { setCurriculumId(event.target.value); setClassIndex(0); }} className={inputClass}>{visibleCurricula.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label>
          {contentMode === 'demo_experience' ? <>
            <div className={`curriculum-admin-editing-status${newDemoIds.has(curriculum.id) ? ' is-new' : ''}`}>
              <span>{newDemoIds.has(curriculum.id) ? 'New unsaved demo' : 'Editing demo'}</span>
              <strong>{curriculum.title}</strong>
              <small>{newDemoIds.has(curriculum.id) ? 'Complete the details, then save or publish it.' : 'Changes below apply to this selected demo.'}</small>
            </div>
            <button type="button" className="curriculum-admin-secondary" style={{ width: '100%', marginTop: 10 }} onClick={addDemoExperience}>+ Create new demo</button>
          </> : isCodeManaged ? <div className="curriculum-admin-editing-status">
            <span>Code-managed lessons</span>
            <strong>{curriculum.levels.length} classes</strong>
            <small>Public details are editable here. Lesson content continues to come from the existing music curriculum files.</small>
          </div> : <div className="curriculum-admin-class-manager">
            <div className="curriculum-admin-classes">{curriculum.levels.map((item, index) => <div
              className={`curriculum-admin-class-row${index === classIndex ? ' active' : ''}${draggedClassIndex === index ? ' is-dragging' : ''}${classDropIndex === index && draggedClassIndex !== index ? ' is-drop-target' : ''}`}
              draggable
              key={item.id}
              onDragStart={(event) => { setDraggedClassIndex(index); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(index)); }}
              onDragEnter={(event) => { event.preventDefault(); if (draggedClassIndex !== null) setClassDropIndex(index); }}
              onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }}
              onDrop={(event) => { event.preventDefault(); const fromIndex = draggedClassIndex ?? Number(event.dataTransfer.getData('text/plain')); moveClass(fromIndex, index); setDraggedClassIndex(null); setClassDropIndex(null); }}
              onDragEnd={() => { setDraggedClassIndex(null); setClassDropIndex(null); }}
            >
              <button type="button" className="curriculum-admin-class-select" onClick={() => setClassIndex(index)}>
                <span className="curriculum-admin-class-handle" aria-hidden="true">⋮⋮</span>
                <span className="curriculum-admin-class-number">{index + 1}</span>
                <span className="curriculum-admin-class-name">{item.title.replace(/^Class\s+\d+:\s*/i, '')}</span>
                <span className="curriculum-admin-class-priority" title="Lesson priority (1 is most important)">P{Number(item.priority) > 0 ? item.priority : index + 1}</span>
              </button>
              <span className="curriculum-admin-class-actions">
                <button type="button" disabled={index === 0} onClick={() => moveClass(index, index - 1)} aria-label={`Move ${item.title} up`}>↑</button>
                <button type="button" disabled={index === curriculum.levels.length - 1} onClick={() => moveClass(index, index + 1)} aria-label={`Move ${item.title} down`}>↓</button>
                <button type="button" className="danger" disabled={curriculum.levels.length === 1} onClick={() => removeClass(index)} aria-label={`Remove ${item.title}`}>×</button>
              </span>
            </div>)}</div>
            <button type="button" className="curriculum-admin-secondary curriculum-admin-add-class" onClick={addClass}>+ Add class</button>
            <small className="curriculum-admin-class-help">Drag classes to reorder them. Changes publish when you save.</small>
          </div>}
        </aside>

        <main className="curriculum-admin-editor">
          <section className="curriculum-admin-card">
            <div className="curriculum-admin-card-title">
              <div><span>{isCodeManaged ? 'Public program listing' : contentMode === 'demo_experience' ? 'Teacher-facing primary tab' : `Class ${classIndex + 1}`}</span><h3>{isCodeManaged ? 'Music program details' : contentMode === 'demo_experience' ? 'Demo Info' : 'Lesson setup'}</h3></div>
              {contentMode === 'demo_experience'
                ? <button type="button" disabled={busy} className={`curriculum-publish-button${curriculum.published !== false ? ' is-published' : ''}`} onClick={() => void setDemoPublished(curriculum.published === false)}>{busy ? 'Saving…' : curriculum.published !== false ? '✓ Published' : 'Publish demo'}</button>
                : <div className="curriculum-admin-card-actions">
                  {!isCodeManaged && <label className="curriculum-admin-priority-control">
                    <span>Lesson priority</span>
                    <input type="number" min="1" step="1" value={Number(level.priority) > 0 ? level.priority : classIndex + 1} onChange={(event) => updateLevel({ priority: Math.max(1, Number(event.target.value) || 1) })} aria-label={`Priority for ${level.title}; 1 is most important`} title="1 is most important; higher numbers are pruned first" />
                  </label>}
                  <span className="curriculum-admin-status">{isCodeManaged ? 'Code-managed lessons' : `${curriculum.levels.length} ${curriculum.levels.length === 1 ? 'class' : 'classes'}`}</span>
                </div>}
            </div>
            <div className="curriculum-admin-grid">
              {contentMode !== 'demo_experience' && <>
                <div className="curriculum-admin-section-heading wide"><span>Public website</span><small>Parent-facing details shown for every program using this curriculum.</small></div>
                <label>Public program name<input className={inputClass} value={curriculum.title} onChange={(e) => updateCurriculum((item) => ({ ...item, title: e.target.value }))} /></label>
                <div
                  className={`curriculum-admin-field curriculum-admin-badge-drop${draggingPublicBadge ? ' is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); if (!uploadingField) setDraggingPublicBadge(true); }}
                  onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = uploadingField ? 'none' : 'copy'; }}
                  onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingPublicBadge(false); }}
                  onDrop={(event) => { event.preventDefault(); setDraggingPublicBadge(false); if (uploadingField) return; const file = event.dataTransfer.files?.[0]; if (file) void uploadPublicBadge(file); }}
                >
                  <span className="curriculum-admin-badge-label">Program badge</span>
                  {curriculum.publicBadgeUrl ? <img className="curriculum-admin-badge-preview" src={curriculum.publicBadgeUrl} alt={`${curriculum.title} program badge`} /> : <span className="curriculum-admin-badge-empty">No badge uploaded</span>}
                  <div className="curriculum-admin-badge-actions">
                    <label className="curriculum-admin-upload-button">{uploadingField === 'public-badge' ? 'Uploading…' : curriculum.publicBadgeUrl ? 'Replace badge' : 'Upload badge'}<input type="file" accept="image/*" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadPublicBadge(file); e.target.value = ''; }} /></label>
                    <small className="curriculum-admin-drop-hint">or drag and drop an image here</small>
                    {curriculum.publicBadgeUrl ? <a href={curriculum.publicBadgeUrl} target="_blank" rel="noreferrer">Open full size ↗</a> : null}
                  </div>
                </div>
                <label className="wide">Public blurb<textarea className={inputClass} rows={3} placeholder="Describe this curriculum for parents and families…" value={curriculum.publicBlurb || ''} onChange={(e) => updateCurriculum((item) => ({ ...item, publicBlurb: e.target.value }))} /></label>
              </>}
              {contentMode === 'demo_experience' && <>
                <div
                  className={`curriculum-admin-field curriculum-reference-primary wide${draggingReferencePdf ? ' is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); if (!uploadingField) setDraggingReferencePdf(true); }}
                  onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = uploadingField ? 'none' : 'copy'; }}
                  onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingReferencePdf(false); }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDraggingReferencePdf(false);
                    if (uploadingField) return;
                    const file = event.dataTransfer.files?.[0];
                    if (file) void uploadReferencePdf(file);
                  }}
                ><span>Main reference PDF</span>
                  <span className="curriculum-admin-file-control">
                    <label className="curriculum-admin-upload-button">{uploadingField === 'reference-pdf' ? 'Uploading…' : curriculum.referencePdf?.url ? 'Replace PDF' : 'Upload PDF'}<input type="file" accept="application/pdf,.pdf" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadReferencePdf(file); e.target.value = ''; }} /></label>
                    {curriculum.referencePdf?.url && <a href={curriculum.referencePdf.url} target="_blank" rel="noreferrer">{curriculum.referencePdf.title} ↗</a>}
                    <small className="curriculum-admin-drop-hint">or drag and drop a PDF here</small>
                  </span>
                  <small className="curriculum-admin-file-name">Shown first on the teacher’s Demo Info tab so the primary guide is always easy to find.</small>
                </div>
                <label>Experience name<input className={inputClass} value={curriculum.title} onChange={(e) => updateCurriculum((item) => ({ ...item, title: e.target.value }))} /></label>
                <label>Category<select className={inputClass} value={curriculum.demoCategory || 'lunch'} onChange={(e) => updateCurriculum((item) => ({ ...item, demoCategory: e.target.value as ManagedCurriculum['demoCategory'] }))}><option value="lunch">Lunch</option><option value="booth">Booth</option><option value="assembly">Assembly</option><option value="classroom">Classroom</option><option value="conference">Conference</option><option value="other">Other</option></select></label>
                <DemoDurationRange min={demoDurationMin} max={demoDurationMax} onChange={(durationMinHours, durationMaxHours) => updateCurriculum((item) => ({ ...item, durationMinHours, durationMaxHours }))} />
                <label className="wide">Experience description<textarea className={inputClass} rows={2} value={curriculum.description || ''} onChange={(e) => updateCurriculum((item) => ({ ...item, description: e.target.value }))} /></label>
                <div className="curriculum-admin-section-heading wide"><span>Teacher-facing class content</span><small>What teachers see when they open this demo experience.</small></div>
              </>}
              {isCodeManaged ? <div className="curriculum-admin-section-heading wide"><span>Lessons remain read-only</span><small>This screen changes the public program listing only. Music classes and activities still come from the code-managed curriculum.</small></div> : <>
              <label>Class title<input className={inputClass} value={level.title} onChange={(e) => updateLevel({ title: e.target.value })} /></label>
              <label>Google Slides deck URL<input className={inputClass} type="url" placeholder="https://docs.google.com/presentation/..." value={level.slideDeckUrl || ''} onChange={(e) => updateLevel({ slideDeckUrl: e.target.value })} /></label>
              <label className="wide">Brief summary<textarea className={inputClass} rows={2} value={level.briefSummary || ''} onChange={(e) => updateLevel({ briefSummary: e.target.value })} /></label>
              <label className="wide">Parent-facing summary (optional)<textarea className={inputClass} rows={3} placeholder="Describe this lesson for families using your curriculum's language." value={level.parentSummary || ''} onChange={(e) => updateLevel({ parentSummary: e.target.value })} /><small>Copy for parent recaps, in place of the brief summary. Leave blank to use the brief summary. Teachers still see the brief summary.</small></label>
              {contentMode !== 'demo_experience' && <label className="wide">I can statement<div className="curriculum-admin-prefix"><span>I can</span><input className={inputClass} value={level.iCanStatement || ''} onChange={(e) => updateLevel({ iCanStatement: e.target.value })} /></div></label>}
              {contentMode !== 'demo_experience' && <>
                <div className="curriculum-admin-section-heading wide"><span>Lesson outcomes</span><small>The thinking habit, memorable refrain, and two examples students will use.</small></div>
                <label>Thinking skill<input className={inputClass} placeholder="e.g. Imagine & Describe" value={level.thinkingSkill || ''} onChange={(e) => updateLevel({ thinkingSkill: e.target.value })} /></label>
                <label>Refrain<input className={inputClass} placeholder="e.g. We have the ideas!" value={level.refrain || ''} onChange={(e) => updateLevel({ refrain: e.target.value })} /></label>
                <div
                  className={`curriculum-admin-field curriculum-admin-example-upload${draggingLessonExample === 'student-artifact-example' ? ' is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); if (!uploadingField) setDraggingLessonExample('student-artifact-example'); }}
                  onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                  onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingLessonExample(null); }}
                  onDrop={(event) => { event.preventDefault(); setDraggingLessonExample(null); if (uploadingField) return; const file = event.dataTransfer.files?.[0]; if (file) void uploadLessonExample(file, 'student-artifact-example'); }}
                >
                  <span>Student artifact example</span>
                  <small>A completed example from the lesson worksheet.</small>
                  <label className="curriculum-admin-example-description">Short description<input className={inputClass} type="text" maxLength={180} placeholder="e.g. A completed product sketch showing labels and customer benefits." value={level.studentArtifactExample?.description || ''} onChange={(event) => updateLessonExampleDescription('studentArtifactExample', event.target.value)} /></label>
                  <label className="curriculum-admin-upload-button">{uploadingField === 'student-artifact-example' ? 'Uploading…' : level.studentArtifactExample?.url ? 'Replace example' : 'Upload example'}<input type="file" accept="application/pdf,image/*,video/*,.doc,.docx" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadLessonExample(file, 'student-artifact-example'); e.target.value = ''; }} /></label>
                  {!level.studentArtifactExample?.url && <span className="curriculum-admin-drop-hint">or drop file here</span>}
                  {level.studentArtifactExample?.url && <a href={level.studentArtifactExample.url} target="_blank" rel="noreferrer">{level.studentArtifactExample.title} ↗</a>}
                </div>
                <div
                  className={`curriculum-admin-field curriculum-admin-example-upload${draggingLessonExample === 'ai-generated-example' ? ' is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); if (!uploadingField) setDraggingLessonExample('ai-generated-example'); }}
                  onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                  onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingLessonExample(null); }}
                  onDrop={(event) => { event.preventDefault(); setDraggingLessonExample(null); if (uploadingField) return; const file = event.dataTransfer.files?.[0]; if (file) void uploadLessonExample(file, 'ai-generated-example'); }}
                >
                  <span>AI-generated example</span>
                  <small>Upload an image, PDF, video, audio, or shot batch ZIP—or paste a link that can be embedded.</small>
                  <label className="curriculum-admin-example-description">Short description<input className={inputClass} type="text" maxLength={180} placeholder="e.g. An AI-generated example of the finished lesson artifact." value={level.aiGeneratedExample?.description || ''} onChange={(event) => updateLessonExampleDescription('aiGeneratedExample', event.target.value)} /></label>
                  <label className="curriculum-admin-example-link">Example URL<input className={inputClass} type="url" placeholder="https://…" value={level.aiGeneratedExample?.kind === 'link' ? level.aiGeneratedExample.url : ''} onChange={(event) => updateAiExampleUrl(event.target.value)} /></label>
                  <div className="curriculum-admin-example-file-row"><span>or</span><label className="curriculum-admin-upload-button">{uploadingField === 'ai-generated-example' ? 'Uploading…' : level.aiGeneratedExample?.url && level.aiGeneratedExample.kind !== 'link' ? 'Replace upload' : 'Upload media or shot batch ZIP'}<input type="file" accept="application/pdf,image/*,video/*,audio/*,.zip,application/zip" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadLessonExample(file, 'ai-generated-example'); e.target.value = ''; }} /></label></div>
                  {!level.aiGeneratedExample?.url && <span className="curriculum-admin-drop-hint">or drop file here</span>}
                  {level.aiGeneratedExample?.kind === 'shot-batch' && level.aiGeneratedExample.url && <div className="curriculum-shot-batch-preview"><ShotBatchExample url={level.aiGeneratedExample.url} /></div>}
                  <small>For a shot batch, choose “Download shot batch” in the portfolio, then upload that ZIP here. Shot order and all options are preserved.</small>
                  {level.aiGeneratedExample?.url && level.aiGeneratedExample.kind !== 'shot-batch' && <a href={level.aiGeneratedExample.url} target="_blank" rel="noreferrer">{level.aiGeneratedExample.title} ↗</a>}
                </div>
              </>}
              <div
                className={`curriculum-admin-field curriculum-admin-video-drop${draggingPreflightVideo ? ' is-dragging' : ''}`}
                onDragEnter={(event) => { event.preventDefault(); if (!uploadingField) setDraggingPreflightVideo(true); }}
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = uploadingField ? 'none' : 'copy'; }}
                onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingPreflightVideo(false); }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDraggingPreflightVideo(false);
                  if (uploadingField) return;
                  const file = event.dataTransfer.files?.[0];
                  if (file) uploadPreflightVideo(file);
                }}
              ><span>Pre-flight video</span>
                <span className="curriculum-admin-file-control">
                  <input type="file" accept="video/*" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadPreflightVideo(file); e.target.value = ''; }} />
                  <span>{uploadingField === 'video' ? 'Uploading…' : level.video?.title || (level.videoUrl ? 'Video uploaded' : 'Choose a video file')}</span>
                  {(level.video?.url || level.videoUrl) && <a href={level.video?.url || level.videoUrl} target="_blank" rel="noreferrer">Preview ↗</a>}
                  <small className="curriculum-admin-drop-hint">or drag and drop a video here</small>
                </span>
              </div>
              {contentMode === 'demo_experience' ? <div className="curriculum-admin-field"><span>Flyer</span>
                <div className="curriculum-admin-worksheet-control">
                  <input className={inputClass} type="url" placeholder="Paste a flyer URL…" value={level.worksheet?.url || ''} onChange={(e) => updateLevel({ worksheet: { id: `${curriculum.id}-${level.id}-worksheet`, title: level.worksheet?.title || 'Demo flyer', kind: 'worksheet', url: e.target.value } })} />
                  <span>or</span>
                  <label className="curriculum-admin-upload-button">
                    {uploadingField === 'worksheet' ? 'Uploading…' : 'Upload flyer'}
                    <input type="file" accept="application/pdf,image/*,.doc,.docx" disabled={uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadClassFile(file, 'worksheet'); e.target.value = ''; }} />
                  </label>
                </div>
                {level.worksheet?.title && level.worksheet.url ? <small className="curriculum-admin-file-name">Current: {level.worksheet.title} · <a href={level.worksheet.url} target="_blank" rel="noreferrer">Open ↗</a></small> : null}
              </div> : <div className="curriculum-admin-field curriculum-admin-worksheet-set wide">
                <span>Worksheets by age group</span>
                <small>Upload differentiated versions, or use K–8 when one worksheet fits every group.</small>
                <div className="curriculum-admin-worksheet-variants">
                  {worksheetAgeGroups.map(({ id: ageGroup, label }) => {
                    const worksheet = level.worksheetsByAgeGroup?.[ageGroup] || (ageGroup === 'k-8' ? level.worksheet : undefined);
                    return <div
                      className={`curriculum-admin-worksheet-variant${draggingWorksheetAgeGroup === ageGroup ? ' is-dragging' : ''}`}
                      key={ageGroup}
                      onDragEnter={(event) => { event.preventDefault(); if (!uploadingWorksheetAgeGroup && !uploadingField) setDraggingWorksheetAgeGroup(ageGroup); }}
                      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingWorksheetAgeGroup(null); }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setDraggingWorksheetAgeGroup(null);
                        if (uploadingWorksheetAgeGroup || uploadingField) return;
                        const file = event.dataTransfer.files?.[0];
                        if (file) void uploadAgeGroupWorksheet(file, ageGroup);
                      }}
                    >
                      <strong>{label}</strong>
                      <input className={inputClass} aria-label={`${label} worksheet URL`} type="url" placeholder="Paste worksheet URL…" value={worksheet?.url || ''} onChange={(e) => setAgeGroupWorksheet(ageGroup, { id: worksheet?.id || `${curriculum.id}-${level.id}-worksheet-${ageGroup}`, title: worksheet?.title || `${label} worksheet`, kind: 'worksheet', url: e.target.value })} />
                      <label className="curriculum-admin-upload-button">
                        {uploadingWorksheetAgeGroup === ageGroup ? 'Uploading…' : worksheet?.url ? 'Replace' : 'Upload'}
                        <input type="file" accept="application/pdf,image/*,.doc,.docx" disabled={uploadingWorksheetAgeGroup !== null || uploadingField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadAgeGroupWorksheet(file, ageGroup); e.target.value = ''; }} />
                      </label>
                      {worksheet?.url ? <a href={worksheet.url} target="_blank" rel="noreferrer" title={worksheet.title}>Open ↗</a> : <span className="curriculum-admin-worksheet-empty">Drop file here</span>}
                    </div>;
                  })}
                </div>
              </div>}
              <div className="wide"><HtmlDocumentEditor label={contentMode === 'demo_experience' ? 'Demo notes' : 'Teacher’s notes'} value={level.teacherNotes || ''} onChange={(teacherNotes) => updateLevel({ teacherNotes })} minHeight={150} previewEnabled={false} placeholders={[]} paperMode={false} placeholder={contentMode === 'demo_experience' ? 'Add instructions, talking points, and reminders for running this demo…' : 'Add the class overview, teaching context, and reminders…'} /></div>
              <div className="wide"><HtmlDocumentEditor label="Additional pre-flight notes" value={level.additionalNotes || ''} onChange={(additionalNotes) => updateLevel({ additionalNotes })} minHeight={110} previewEnabled={false} placeholders={[]} paperMode={false} placeholder="Add setup details teachers should review before class…" /></div>
              </>}
            </div>
          </section>

          {!isCodeManaged && <><section className="curriculum-admin-card">
            <div className="curriculum-admin-card-title"><div><span>Pre-flight checklist</span><h3>Resources & materials</h3></div><button type="button" className="curriculum-admin-secondary" onClick={addResource}>+ Add resource</button></div>
            <p className="curriculum-admin-help">These appear prominently behind the Resources button on the class board.</p>
            <div className="curriculum-admin-resources">
              {(level.resources || []).map((resource, index) => <div className="curriculum-admin-resource" key={resource.id}>
                <select className={inputClass} value={resource.kind || 'link'} onChange={(e) => updateResource(index, { kind: e.target.value as any })}><option value="material">Material</option><option value="download">Download</option><option value="link">External link</option><option value="video">Video</option></select>
                <input className={inputClass} aria-label="Resource title" value={resource.title} onChange={(e) => updateResource(index, { title: e.target.value })} />
                <div
                  className={`curriculum-admin-resource-drop${draggingResourceIndex === index ? ' is-dragging' : ''}${uploadingResourceIndex === index ? ' is-uploading' : ''}`}
                  onDragEnter={(e) => { e.preventDefault(); if (uploadingResourceIndex === null) setDraggingResourceIndex(index); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDraggingResourceIndex(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggingResourceIndex(null);
                    const file = e.dataTransfer.files?.[0];
                    if (file && uploadingResourceIndex === null) void uploadResource(index, file);
                  }}
                >
                  <input className={inputClass} aria-label="Resource URL" type="url" placeholder={uploadingResourceIndex === index ? 'Uploading file…' : 'Paste URL or drop a file here'} value={resource.url} disabled={uploadingResourceIndex === index} onChange={(e) => updateResource(index, { url: e.target.value })} />
                </div>
                <button type="button" aria-label="Remove resource" onClick={() => removeResource(index)}>×</button>
              </div>)}
              {!(level.resources || []).length && <div className="curriculum-admin-empty">No additional resources yet. Add supply lists, downloads, or external links.</div>}
            </div>
          </section>

          {contentMode === 'demo_experience' && !(level.missions || []).length ? <button type="button" className="curriculum-admin-secondary" style={{ width: '100%', padding: 16 }} onClick={addLessonLoop}>+ Add a lesson loop</button> : <section className={`curriculum-admin-card curriculum-admin-loop${lessonLoopExpanded ? ' is-expanded' : ''}`}>
            <div className="curriculum-admin-card-title curriculum-admin-loop-title">
              <button type="button" className="curriculum-admin-accordion-trigger" aria-expanded={lessonLoopExpanded} aria-controls="curriculum-lesson-loop" onClick={() => setLessonLoopExpanded((expanded) => !expanded)}>
                <span className="curriculum-admin-chevron" aria-hidden="true">›</span>
                <span className="curriculum-admin-accordion-copy"><span>Lesson loop · {(level.missions || []).length} activities</span><strong>Activities & facilitation notes</strong></span>
              </button>
              <button type="button" className="curriculum-admin-secondary" onClick={addMission}>+ Add activity</button>
            </div>
            {lessonLoopExpanded && <div id="curriculum-lesson-loop" className="curriculum-admin-missions">{(level.missions || []).map((mission: any, index: number) => {
              const missionId = String(mission.id);
              const expanded = expandedMissionIds.has(missionId);
              const title = mission.title || String(mission.text || '').replace(/\s*\([^)]*?\d+\s*min[^)]*\)\s*$/i, '') || 'Untitled activity';
              const minutes = mission.plannedMinutes || Number(String(mission.text || '').match(/(\d+)\s*min/i)?.[1]) || 10;
              const contentId = `curriculum-activity-${classIndex}-${index}`;
              return <article className={expanded ? 'is-expanded' : ''} key={missionId} ref={(element) => { if (element) missionElements.current.set(missionId, element); else missionElements.current.delete(missionId); }}>
                <button type="button" className="curriculum-admin-mission-summary" aria-expanded={expanded} aria-controls={contentId} onClick={() => toggleMission(missionId)}>
                  <span className="curriculum-admin-chevron" aria-hidden="true">›</span>
                  <span className="curriculum-admin-mission-number">{index + 1}</span>
                  <span className="curriculum-admin-mission-summary-copy"><strong>{title}</strong><span>{String(mission.missionType || 'activity').replace('-', ' ')} · {minutes} min</span></span>
                  <span className="curriculum-admin-mission-edit">{expanded ? 'Close' : 'Edit'}</span>
                </button>
                {expanded && <div id={contentId} className="curriculum-admin-mission-content">
                  <div className="curriculum-admin-mission-fields">
                    <div className="curriculum-admin-mission-heading">
                      <select aria-label="Activity type" className={inputClass} value={mission.missionType || 'activity'} onChange={(e) => updateMission(index, { missionType: e.target.value })}>
                        <option value="warm-up">Warm-up</option><option value="welcome">Intro / welcome</option><option value="reveal">Reveal</option><option value="activity">Activity</option><option value="capture">Capture</option><option value="reflect">Reflection</option><option value="other">Other</option>
                      </select>
                      <div className="curriculum-admin-mission-order">
                        <button type="button" disabled={index === 0} onClick={() => moveMission(index, -1)} aria-label="Move activity up">↑</button>
                        <button type="button" disabled={index === (level.missions || []).length - 1} onClick={() => moveMission(index, 1)} aria-label="Move activity down">↓</button>
                        <button type="button" className="danger" onClick={() => removeMission(index)} aria-label="Remove activity">Remove</button>
                      </div>
                    </div>
                    <label>Activity name<input data-activity-name className={inputClass} value={title === 'Untitled activity' ? '' : title} onChange={(e) => updateMission(index, { title: e.target.value, text: e.target.value })} /></label>
                    <label>Duration (minutes)<input className={inputClass} type="number" min="1" max="180" value={minutes} onChange={(e) => updateMission(index, { plannedMinutes: Math.max(1, Number(e.target.value) || 1) })} /></label>
                  </div>
                  <div className="curriculum-admin-rich-notes">
                    <HtmlDocumentEditor label="Facilitation notes" value={mission.teachingNotes || ''} onChange={(teachingNotes) => updateMission(index, { teachingNotes })} minHeight={150} previewEnabled={false} placeholders={[]} paperMode={false} placeholder="Add facilitation notes, key questions, transitions, or reminders…" />
                    <div className="curriculum-admin-activity-files">
                      <span className="curriculum-admin-activity-files-label">Activity files</span>
                      <div className="curriculum-admin-activity-upload-actions">
                        <label className="curriculum-admin-upload-button">
                          {uploadingActivity?.missionId === missionId && uploadingActivity.kind === 'video' ? 'Uploading video…' : '+ Upload video'}
                          <input type="file" accept="video/*" disabled={uploadingActivity !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadActivityFile(index, file, 'video'); e.target.value = ''; }} />
                        </label>
                        <label className="curriculum-admin-upload-button">
                          {uploadingActivity?.missionId === missionId && uploadingActivity.kind === 'document' ? 'Uploading document…' : '+ Upload document'}
                          <input type="file" accept="application/pdf,image/*,.doc,.docx" disabled={uploadingActivity !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadActivityFile(index, file, 'document'); e.target.value = ''; }} />
                        </label>
                      </div>
                      {(mission.attachments || []).map((attachment: CurriculumResource, attachmentIndex: number) => <div className="curriculum-admin-activity-file" key={attachment.id || `${attachment.url}-${attachmentIndex}`}>
                        <span className={`curriculum-admin-file-kind is-${attachment.kind || 'document'}`}>{attachment.kind === 'video' ? 'Video' : 'Document'}</span>
                        <a href={attachment.url} target="_blank" rel="noreferrer">{attachment.title}</a>
                        <button type="button" onClick={() => removeActivityAttachment(index, attachmentIndex)} aria-label={`Remove ${attachment.title}`}>×</button>
                      </div>)}
                    </div>
                    {(mission.missionType === 'reveal' || mission.missionType === 'reflect') && <small>Native Overture activity support can be added here later.</small>}
                  </div>
                </div>}
              </article>;
            })}</div>}
          </section>}</>}
        </main>
      </div>
    </div>
  );
};

export default AdminCurriculumTab;
